import * as React from "react";

import { LabelGroup } from "../label-group";
import { Card } from "../card";
import { Badge } from "@dropbox/dig-components/badge";
import { Box, Split } from "@dropbox/dig-foundations";
import classNames from "classnames";
import styles from "./ContentRow.module.css";
import { ContentRowProps } from "./types";
import {
  convertPaddingSizeToBreakoutSize,
  convertPaddingSizeToPaddingX,
} from "./utils";

/**
 * A row component that displays content with optional accessories and content.
 * It can be used as a clickable item and supports both desktop and mobile layouts.
 */
export const ContentRow: React.FC<ContentRowProps> = ({
  /**
   * Whether to display a small attention badge on the left side of the row
   * @default false
   */
  showBadge = false,

  /**
   * The main label content to display in the row
   */
  withLabel,

  /**
   * Additional meta information to display alongside the label
   */
  withMeta,

  /**
   * An accessory component to display at the start of the row
   */
  withStartAccessory,

  /**
   * Actions to display on the right side of the row
   */
  withActions,

  /**
   * Additional props to pass to the LabelGroup component
   */
  labelGroupProps,

  /**
   * The padding size of the row
   * @default "large"
   */
  paddingSize = "large",

  /**
   * Callback function when the row is clicked
   */
  onClick,

  /**
   * URL to display when the row is hovered (if clickable)
   */
  visibleUrl,

  /**
   * Whether to use mobile-optimized layout
   * @default false
   */
  isMobile = false,

  /**
   * The variant of the card look.
   * @default outline
   */
  variant = "outline",

  /**
   * A CSS class to apply to this content.
   */
  className,

  /**
   * Inline CSS styles.
   */
  style,
}) => {
  const breakoutSize = convertPaddingSizeToBreakoutSize(paddingSize);
  const paddingX = convertPaddingSizeToPaddingX(paddingSize);

  return (
    <Card.Module
      breakout={breakoutSize}
      isLink={!!onClick}
      variant={variant}
      className={className}
      style={style}
    >
      <Split
        direction={isMobile ? "vertical" : "horizontal"}
        alignY="center"
        alignX="center"
      >
        <Split.Item
          className={styles.mainContent}
          width="fill"
          paddingX={paddingX}
        >
          {showBadge && (
            <Badge
              size="xsmall"
              variant="attention"
              className={styles.unreadBadge}
            />
          )}
          {onClick ? (
            <Card.OverlayLink onClick={onClick} href={visibleUrl ?? "#"}>
              <LabelGroup
                verticalAlignment="center"
                accessoryGap="Micro XLarge"
                className={styles.label}
                {...{
                  ...labelGroupProps,
                  withLabel,
                  withMeta,
                  withStartAccessory,
                }}
              />
            </Card.OverlayLink>
          ) : (
            <LabelGroup
              verticalAlignment="center"
              accessoryGap="Micro XLarge"
              className={styles.label}
              {...{
                ...labelGroupProps,
                withLabel,
                withMeta,
                withStartAccessory,
              }}
            />
          )}
        </Split.Item>
        <Split.Item>
          {withActions && (
            <Box
              className={classNames(styles.actions, {
                [styles.mobileActions]: isMobile,
              })}
            >
              {withActions}
            </Box>
          )}
        </Split.Item>
      </Split>
    </Card.Module>
  );
};
